import React, { useContext, useEffect, useState } from 'react';
import { getResourceByGuid, getResourcesStatus } from './api';
import { AuthenticationContext } from './AuthenticationProvider';
import { ResourcesStatus } from './types/ResourcesStatusResponse';
import { ResourceType } from './types/Resource';
import { ActionTypes } from './types/ResourceAction';
import moment from 'moment/moment';
import { ActionCableContext } from './ActionCableProvider';
import { trackPromise } from 'react-promise-tracker';
import { ResourceResponse } from './types/ResourceResponse';

export type ResourceHistory = {
  action: ActionTypes;
  userName: string;
  message: string;
  date: string;
};

export type ResourceData = {
  localId: string;
  name: string;
  guid: string;
  parentGuid: string;
  resourceType: ResourceType;
  status: 'available' | 'checked_out' | 'deleted';
  byEmail: undefined | string;
  byUser: undefined | string;
  onDate: undefined | string;
  history: ResourceHistory[];
};

export interface IResourcesContext {
  resources: ResourceData[];
  content: string | undefined;

  fetchResources(): void;
  onGetContent(_guid: string): void;
}

export interface IResourcesProviderProps {
  children: any;
  projectId: number;
}

const getStatus = (resource: ResourcesStatus) => {
  if (
    resource.last_action &&
    resource.last_action.action_type === 'check_out'
  ) {
    return 'checked_out';
  }

  return 'available';
};

const createResourceData = (resources: ResourcesStatus[]) => {
  let arr: ResourceData[] = [];
  resources.forEach((resource) => {
    // Hide deleted resources to avoid bugs related
    // to duplicated local IDs.
    if (resource.last_action.action_type === 'destroy') {
      return;
    }

    let obj: ResourceData = {
      localId: resource.local_id,
      name: resource.name,
      guid: resource.resource_guid,
      parentGuid: resource.parent_guid,
      resourceType: resource.resource_type,
      status: getStatus(resource),
      byUser:
        resource.last_action && resource.last_action.action_type === 'check_out'
          ? `${resource.last_action.user.first_name} ${resource.last_action.user.last_name}`
          : '',
      byEmail:
        resource.last_action && resource.last_action.action_type === 'check_out'
          ? resource.last_action.user.email
          : '',
      onDate:
        resource.last_action && resource.last_action.action_type === 'check_out'
          ? moment(resource.last_action.created_at).format('LLL')
          : '',
      history: [],
    };

    resource.resource_actions.forEach((action) => {
      obj.history.push({
        action: action.action_type,
        userName: `${action.user.first_name} ${action.user.last_name}`,
        message: action.action_message,
        date: action.created_at,
      });
    });
    arr.push(obj);
  });

  return arr;
};

const ResourcesContext = React.createContext<IResourcesContext>({
  resources: [],
  content: undefined,

  fetchResources() {},

  onGetContent(_guid: string) {},
});

const ResourcesProvider = (props: IResourcesProviderProps) => {
  const { children, projectId } = props;

  const { token } = useContext(AuthenticationContext);
  const { onSubscribe, onClearMessages, messages, available } =
    useContext(ActionCableContext);

  const [fetch, setFetch] = useState(true);
  const [resources, setResources] = useState<ResourceData[]>([]);
  const [subscribed, setSubscribed] = useState<boolean>(false);
  const [content, setContent] = useState<string | undefined>();

  useEffect(() => {
    if (fetch && token && projectId) {
      trackPromise(getResourcesStatus(projectId, token))
        .then((resourceData) => {
          if (resourceData) {
            setResources(createResourceData(resourceData.resource_status));
            setFetch(false);
          }
        })
        .catch((err) => {
          console.log(err);
        });
    }
  }, [fetch, projectId, token]);

  useEffect(() => {
    if (projectId && !subscribed && available) {
      onSubscribe(Number(projectId));
      setSubscribed(true);
    }
  }, [projectId, subscribed, onSubscribe, available]);

  useEffect(() => {
    if (messages[projectId] && messages[projectId].length > 0) {
      setFetch(true);
      onClearMessages(projectId);
    }
  }, [messages, projectId, onClearMessages]);

  const fetchResources = () => setFetch(true);

  const onGetContent = (guid: string) => {
    if (token) {
      setContent(undefined);
      trackPromise(getResourceByGuid(guid, token))
        .then((resource: ResourceResponse) => {
          setContent(resource.resource.resource_content);
        })
        .catch((err: any) => console.log(err));
    }
  };

  return (
    <ResourcesContext.Provider
      value={{
        resources,
        content,

        fetchResources,
        onGetContent,
      }}
    >
      {children}
    </ResourcesContext.Provider>
  );
};

export { ResourcesContext, ResourcesProvider };
